import { useEffect, useState } from "react"

import DownloadIcon from '@mui/icons-material/Download'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material'

import { handleApiError, profilesService } from "../../api"
import { triggerBrowserDownload } from "../../lib/export-utils"

import type { PatientProfileExportRecord } from "../../api"

const csvEscape = (value: unknown): string => {
  const str = value == null ? '' : String(value)
  const needsQuoting = /[",\n]/.test(str)
  const escaped = str.replace(/"/g, '""')
  return needsQuoting ? `"${escaped}"` : escaped
}

const CSV_COLUMNS: { header: string; value: (r: PatientProfileExportRecord) => string }[] = [
  { header: 'Patient', value: r => r.patientLabel },
  { header: 'User ID', value: r => r.userId },
  { header: 'Age', value: r => r.age ?? '' },
  { header: 'Sex', value: r => r.sex ?? '' },
  { header: 'Weight', value: r => r.weight ?? '' },
  { header: 'Height', value: r => r.height ?? '' },
  { header: 'Blood Pressure Systolic', value: r => r.bloodPressureSystolic ?? '' },
  { header: 'Blood Pressure Diastolic', value: r => r.bloodPressureDiastolic ?? '' },
  { header: 'Health Goals', value: r => r.healthGoals.join('; ') },
  { header: 'Health Concerns', value: r => r.healthConcerns.join('; ') },
  { header: 'Smoking', value: r => r.smoking ?? '' },
  { header: 'Focus', value: r => r.focusType ?? '' },
  { header: 'Medications', value: r => r.medications ?? '' },
  { header: 'Supplements', value: r => r.supplements ?? '' },
  { header: 'Family History', value: r => r.familyHistory ?? '' },
  { header: 'Active Symptoms', value: r => r.activeSymptoms ?? '' },
  { header: 'Previous Labs', value: r => r.previousLabs ?? '' },
  { header: 'Tests of Interest', value: r => r.testsOfInterest ?? '' },
  { header: 'Insurance', value: r => r.insurance ?? '' },
]

export default function PatientProfileExportCard() {
  const [records, setRecords] = useState<PatientProfileExportRecord[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")

  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setIsLoading(true)
        setError("")
        const data = await profilesService.getFilledPatientProfilesForExport()
        setRecords(data)
      } catch (err) {
        setError(handleApiError(err))
      } finally {
        setIsLoading(false)
      }
    }

    fetchRecords()
  }, [])

  const handleExportCsv = () => {
    const headers = CSV_COLUMNS.map(c => c.header)
    const rows = records.map(r => CSV_COLUMNS.map(c => csvEscape(c.value(r))).join(','))
    const bom = '\ufeff'
    const csv = [headers.join(','), ...rows].join('\n')
    const blob = new Blob([bom + csv], { type: 'text/csv;charset=utf-8;' })
    const dateStamp = new Date().toISOString().split('T')[0]
    triggerBrowserDownload({ blob, filename: `patient-health-medical-export-${dateStamp}.csv` })
  }

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          Patient Health &amp; Medical Export
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Anonymized export of Health Profile and Medical Information only. No names or emails: each patient is labelled sequentially with their internal user ID for cross-reference. Only patients who have filled their health and medical profile are included.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body1" color="text.secondary">Loading…</Typography>
            </Box>
          ) : (
            <Typography variant="body1" color="text.secondary">
              {records.length} patients with a filled health &amp; medical profile
            </Typography>
          )}

          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={handleExportCsv}
            disabled={isLoading || records.length === 0}
          >
            Export CSV
          </Button>
        </Box>
      </CardContent>
    </Card>
  )
}
